import { useState, useEffect, useCallback } from "react";
import {
  X, Mail, Phone, Calendar, Clock, FileText, Hash, Package,
  MessageCircle, Zap, StickyNote, Trash2, Send, Crown, ShoppingBag, UserPlus
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Sheet, SheetContent } from "../ui/sheet";
import {
  BACKEND_URL, getAuthHeaders, formatDate, getRelativeTime,
  isUserLive, getUserTagType, TAG_CONFIG, openWhatsApp, openEmail, WHATSAPP_MESSAGE
} from "./adminHelpers";

const TAG_ICONS = { vip: Crown, purchased: ShoppingBag, new: UserPlus };

export const UserDetailPanel = ({ user: selectedUser, isOpen, onClose, onUserUpdated }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const fetchNotes = useCallback(async () => {
    if (!selectedUser) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/notes`, {
        credentials: "include", headers: getAuthHeaders(),
      });
      if (res.ok) setNotes(await res.json());
    } catch (e) { console.error("Failed to fetch notes:", e); }
  }, [selectedUser]);

  useEffect(() => {
    if (isOpen && selectedUser) {
      fetchNotes();
      setActiveTab("details");
      setNewNote("");
    }
  }, [isOpen, selectedUser, fetchNotes]);

  const addNote = async () => {
    if (!newNote.trim() || !selectedUser) return;
    setIsSavingNote(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/notes`, {
        method: "POST", credentials: "include", headers: getAuthHeaders(),
        body: JSON.stringify({ content: newNote.trim() }),
      });
      if (res.ok) {
        const note = await res.json();
        setNotes([note, ...notes]);
        setNewNote("");
      }
    } catch (e) { console.error("Failed to add note:", e); }
    finally { setIsSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    if (!selectedUser) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/notes/${noteId}`, {
        method: "DELETE", credentials: "include", headers: getAuthHeaders(),
      });
      if (res.ok) setNotes(notes.filter(n => n.note_id !== noteId));
    } catch (e) { console.error("Failed to delete note:", e); }
  };

  if (!selectedUser) return null;

  const tagType = getUserTagType(selectedUser);
  const tag = TAG_CONFIG[tagType];
  const TagIcon = TAG_ICONS[tagType];
  const live = isUserLive(selectedUser.last_login);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="left" className="w-full sm:max-w-md p-0 bg-[#FAF8F5] dark:bg-[#0f1510] overflow-y-auto" dir="rtl">
        {/* Header */}
        <div className="bg-white dark:bg-[#1a231a] border-b border-[#2C3329]/10 dark:border-[#2a3a2a] p-6">
          <div className="flex items-start gap-4">
            <div className="relative">
              <Avatar className="h-16 w-16 border-2 border-[#2C3329]/10">
                <AvatarImage src={selectedUser.picture} alt={selectedUser.name} />
                <AvatarFallback className="bg-[#4A5D43]/10 text-[#4A5D43] font-serif text-xl">{selectedUser.name?.charAt(0) || '?'}</AvatarFallback>
              </Avatar>
              {live && <div className="absolute bottom-0 right-0 w-4 h-4 bg-[#6B8E6B] rounded-full border-2 border-white dark:border-[#1a231a]" />}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 data-testid="detail-panel-name" className="font-serif text-xl text-[#2C3329] dark:text-white truncate">{selectedUser.name || 'ללא שם'}</h2>
                <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-sm text-[10px] font-medium ${tag.bg} ${tag.text}`}>
                  {TagIcon && <TagIcon className="h-2.5 w-2.5" />}{tag.label}
                </span>
              </div>
              <p className="font-sans text-sm text-[#5C5C5C] truncate">{selectedUser.email}</p>
              {live && <p className="font-sans text-xs text-[#6B8E6B] mt-1 flex items-center gap-1"><Zap className="h-3 w-3" />כרגע באתר</p>}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 mt-4">
            {selectedUser.phone && (
              <Button data-testid="detail-whatsapp-btn" size="sm" className="flex-1 bg-[#6B8E6B] hover:bg-[#5a7d5a] text-white rounded-sm text-xs"
                onClick={() => openWhatsApp(selectedUser.phone, WHATSAPP_MESSAGE)}>
                <MessageCircle className="h-3.5 w-3.5 me-1" />WhatsApp
              </Button>
            )}
            <Button data-testid="detail-email-btn" size="sm" variant="outline" className="flex-1 border-[#2C3329]/15 text-[#2C3329] hover:bg-[#2C3329]/5 rounded-sm text-xs"
              onClick={() => openEmail(selectedUser.email, selectedUser.name)}>
              <Mail className="h-3.5 w-3.5 me-1" />אימייל
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#2C3329]/10 dark:border-[#2a3a2a] bg-white dark:bg-[#1a231a]">
          {["details", "notes", "history"].map((tab) => (
            <button
              key={tab}
              data-testid={`detail-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 font-sans text-xs tracking-[0.03em] uppercase transition-colors border-b-2 ${
                activeTab === tab
                  ? 'border-[#4A5D43] text-[#2C3329] dark:text-white font-medium'
                  : 'border-transparent text-[#A3A3A3] hover:text-[#5C5C5C]'
              }`}
            >
              {tab === "details" ? "פרטים" : tab === "notes" ? `הערות (${notes.length})` : "היסטוריה"}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-5">
          {activeTab === "details" && <DetailsTab user={selectedUser} />}
          {activeTab === "notes" && (
            <NotesTab
              notes={notes}
              newNote={newNote}
              setNewNote={setNewNote}
              onAddNote={addNote}
              onDeleteNote={deleteNote}
              isSaving={isSavingNote}
            />
          )}
          {activeTab === "history" && <HistoryTab user={selectedUser} />}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const DetailsTab = ({ user }) => (
  <div className="space-y-4">
    <DetailRow icon={Phone} label="טלפון" value={user.phone || "לא צוין"} dir="ltr" />
    <DetailRow icon={Calendar} label="הרשמה" value={formatDate(user.created_at)} />
    <DetailRow icon={Clock} label="פעילות אחרונה" value={user.last_login ? getRelativeTime(user.last_login) : "לא התחבר"} />
    <DetailRow icon={Zap} label="כניסות" value={`${user.login_count || 0} כניסות`} />

    <div className="border-t border-[#2C3329]/8 pt-4 mt-4">
      <h4 className="font-sans text-[11px] tracking-[0.05em] uppercase text-[#A3A3A3] mb-3">סטטוס</h4>
      <div className="grid grid-cols-2 gap-3">
        <StatusBox label="ערכת ניסיון" active={user.received_trial_kit} icon={Package} />
        <StatusBox label="נוצר קשר" active={user.whatsapp_contacted} icon={MessageCircle} />
        <StatusBox
          label="PDF"
          active={user.pdf_download_count > 0}
          icon={FileText}
          detail={user.pdf_download_count > 0 ? `${user.pdf_download_count} הורדות` : null}
        />
        <StatusBox
          label="קופון"
          active={!!user.coupon_code}
          icon={Hash}
          detail={user.coupon_code ? `${user.coupon_code}${user.coupon_used ? ' (נוצל)' : ''}` : null}
        />
      </div>
    </div>
  </div>
);

const DetailRow = ({ icon: Icon, label, value, dir }) => (
  <div className="flex items-center gap-3">
    <div className="p-2 rounded-sm bg-[#2C3329]/5 dark:bg-[#4A5D43]/10">
      <Icon className="h-4 w-4 text-[#4A5D43]" />
    </div>
    <div className="min-w-0">
      <p className="font-sans text-[11px] tracking-[0.03em] text-[#A3A3A3]">{label}</p>
      <p className="font-sans text-sm text-[#2C3329] dark:text-white truncate" dir={dir}>{value}</p>
    </div>
  </div>
);

const StatusBox = ({ label, active, icon: Icon, detail }) => (
  <div className={`p-3 rounded-sm border transition-colors ${active ? 'border-[#4A5D43]/20 bg-[#4A5D43]/5' : 'border-[#2C3329]/8 bg-white dark:bg-[#1a231a]'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`h-3.5 w-3.5 ${active ? 'text-[#4A5D43]' : 'text-[#A3A3A3]'}`} />
      <span className={`font-sans text-xs font-medium ${active ? 'text-[#4A5D43]' : 'text-[#A3A3A3]'}`}>{label}</span>
    </div>
    {detail && <p className="font-sans text-[11px] text-[#5C5C5C] mt-0.5">{detail}</p>}
    {!detail && <p className="font-sans text-[11px] text-[#A3A3A3]">{active ? 'כן' : 'לא'}</p>}
  </div>
);

const NotesTab = ({ notes, newNote, setNewNote, onAddNote, onDeleteNote, isSaving }) => (
  <div className="space-y-4">
    {/* Add Note */}
    <div className="bg-white dark:bg-[#1a231a] rounded-sm border border-[#2C3329]/10 p-3">
      <textarea
        data-testid="add-note-textarea"
        value={newNote}
        onChange={(e) => setNewNote(e.target.value)}
        placeholder="הוסף הערה..."
        rows={3}
        className="w-full font-sans text-sm text-[#2C3329] dark:text-white bg-transparent resize-none focus:outline-none placeholder:text-[#A3A3A3]"
      />
      <div className="flex justify-end mt-2">
        <Button
          data-testid="save-note-btn"
          size="sm"
          onClick={onAddNote}
          disabled={!newNote.trim() || isSaving}
          className="bg-[#2C3329] hover:bg-[#4A5D43] text-[#FAF8F5] rounded-sm text-xs disabled:opacity-40"
        >
          <Send className="h-3 w-3 me-1" />{isSaving ? 'שומר...' : 'שמור'}
        </Button>
      </div>
    </div>

    {/* Notes List */}
    {notes.length === 0 ? (
      <div className="text-center py-8">
        <StickyNote className="h-8 w-8 text-[#A3A3A3] mx-auto mb-2" />
        <p className="font-sans text-sm text-[#A3A3A3]">אין הערות עדיין</p>
      </div>
    ) : (
      <div className="space-y-2">
        {notes.map((note) => (
          <div key={note.note_id} className="bg-white dark:bg-[#1a231a] rounded-sm border border-[#2C3329]/10 p-3 group">
            <p className="font-sans text-sm text-[#2C3329] dark:text-white whitespace-pre-wrap">{note.content}</p>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#2C3329]/5">
              <span className="font-sans text-[10px] text-[#A3A3A3]">{formatDate(note.created_at)}</span>
              <button
                data-testid={`delete-note-${note.note_id}`}
                onClick={() => onDeleteNote(note.note_id)}
                className="opacity-0 group-hover:opacity-100 text-[#C67D63] hover:text-[#a5604c] transition-all p-1 rounded-sm hover:bg-[#C67D63]/10"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const HistoryTab = ({ user }) => {
  const history = (user.login_history || []).slice().reverse().slice(0, 20);
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-4 w-4 text-[#4A5D43]" />
        <span className="font-sans text-sm font-medium text-[#2C3329] dark:text-white">{user.login_count || 0} כניסות</span>
      </div>
      {history.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="h-8 w-8 text-[#A3A3A3] mx-auto mb-2" />
          <p className="font-sans text-sm text-[#A3A3A3]">אין היסטוריית כניסות</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute right-[11px] top-2 bottom-2 w-px bg-[#2C3329]/10 dark:bg-[#2a3a2a]" />
          <div className="space-y-3">
            {history.map((login, i) => (
              <div key={i} className="flex items-start gap-3 relative">
                <div className={`w-[23px] h-[23px] rounded-full border-2 flex items-center justify-center shrink-0 z-10 ${
                  i === 0 ? 'border-[#4A5D43] bg-[#4A5D43]/10' : 'border-[#2C3329]/15 bg-white dark:bg-[#1a231a]'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-[#4A5D43]' : 'bg-[#2C3329]/20'}`} />
                </div>
                <div className="pt-0.5">
                  <p className="font-sans text-sm text-[#2C3329] dark:text-white">{formatDate(login)}</p>
                  <p className="font-sans text-xs text-[#A3A3A3]">{getRelativeTime(login)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
